import { type ActionMeta } from '@databases/atlaskit/Select';
import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { type BlogpostReference, type PageReference } from '@databases/types/confluence';
import { type DatabaseFieldType } from '@databases/types/database';
import { getContentKey } from '@databases/utils/confluence/utils';
import {
  type PageLabelsDelta,
  applyPageLabels,
  getContentLabels,
  getReferencedLabelContentRefs,
} from '@databases/utils/database/field-types/pageLabels';
import { getDatabaseEntryRequired } from '@databases/utils/database/utils';
import { getLabelsDelta } from '@databases/utils/deltas/labels-delta';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { PopoverEdit } from '../PopoverEdit';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection';
import { type EditComponentProps } from '../types';

import PageLabelsSelect, { type PageLabelsSelectOption } from './PageLabelsSelect';

const Container = styled.ul`
  list-style-type: none;
  padding: 0;
`;

function applyLabelsDeltas(labels: string[], deltas: PageLabelsDelta[]): string[] {
  return deltas.reduce(
    (p, c) => [...new Set([...p, ...c.add].filter((l) => !c.remove.includes(l)))],
    labels
  );
}

const PageLabelsPopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.PAGE_LABELS>
> = ({
  onFocus,
  onBlur,
  value,
  field,
  entryId,
  contentId,
  autoFocus,
  isInvalid,
  isSeamless,
  isFilterValueEdit,
  isDefaultValue,
  isBulkEdit,
}) => {
  const contentRefs = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: true,
    });
    const entry = entryId && getDatabaseEntryRequired(databaseDraft, entryId);
    return entry
      ? getReferencedLabelContentRefs({ database: databaseDraft, field, entry }, state)
      : [];
  });
  const isRenderedInSidePanel = Boolean(isDefaultValue || isFilterValueEdit);
  usePopoverSoftSelection({
    entryId,
    fieldId: field.id,
    isRenderedInSidePanel,
  });

  const AddDataSection = useMemo(
    () => (
      <Container>
        {contentRefs.map((contentRef, i) => {
          return (
            <li key={`${getContentKey(contentRef)}${i}`}>
              <SinglePageLabelEdit
                {...{
                  contentRef,
                  value,
                  onFocus,
                  onBlur,
                  autoFocus,
                  isInvalid,
                  isSeamless,
                }}
              />
            </li>
          );
        })}
      </Container>
    ),
    [contentRefs, onFocus, onBlur, autoFocus, isInvalid, isSeamless, value]
  );

  return (
    <PopoverEdit
      isMultiSelect={false}
      hasCurrentData={false}
      addDataSection={AddDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
      isBulkEdit={isBulkEdit}
    />
  );
};

export default PageLabelsPopoverEdit;

interface SinglePageLabelEditProps
  extends Pick<
    EditComponentProps<DatabaseFieldType.PAGE_LABELS>,
    'value' | 'onFocus' | 'onBlur' | 'autoFocus' | 'isInvalid' | 'isSeamless'
  > {
  contentRef: PageReference | BlogpostReference;
}

const SinglePageLabelEdit: FunctionComponent<SinglePageLabelEditProps> = ({
  contentRef,
  value,
  onFocus,
  onBlur,
  autoFocus,
  isInvalid,
  isSeamless,
}) => {
  const { formatMessage } = useIntl();

  const [labels] = defaultStoreUtils.useEqualSelector((state) => {
    return [getContentLabels(contentRef, value, state) ?? []] as const;
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [pendingDeltas, setPendingDeltas] = useState<PageLabelsDelta[]>([]);
  const resolvedLabels = useMemo(
    () => applyLabelsDeltas(labels, pendingDeltas),
    [labels, pendingDeltas]
  );

  const store = defaultStoreUtils.useStore();
  const handleChange = useCallback(
    async (_: string[], action: ActionMeta<PageLabelsSelectOption>) => {
      const delta = getLabelsDelta(action);
      if (delta) {
        setPendingDeltas((deltas) => [...deltas, delta]);
        try {
          await applyPageLabels(contentRef, delta, store);
        } catch (e: unknown) {
          setErrorMessage(
            formatMessage(messages['field-settings.type-page-label-label-create-error'])
          );
          return;
        } finally {
          setPendingDeltas((deltas) => deltas.filter((d) => d !== delta));
        }
      }
    },
    [contentRef, formatMessage, store]
  );

  const handleFocus = useCallback(
    (e?: React.FocusEvent<HTMLElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e?: React.FocusEvent<HTMLElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  return (
    <ValidationStatus error={errorMessage} isTouched={isFocused}>
      {(props) => (
        <PageLabelsSelect
          {...props}
          onInputChange={() => setErrorMessage('')}
          value={resolvedLabels}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          isInvalid={isInvalid}
          isSeamless={isSeamless}
          isLoading={pendingDeltas.length > 0}
          linkedPageContentId={contentRef.contentId}
        />
      )}
    </ValidationStatus>
  );
};
